import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {memo, type RefObject, useCallback, useId} from 'react'

import {SystemColumnId} from '../../../api/columns/contracts/memex-column'
import {ItemType} from '../../../api/memex-items/item-type'
import type {BoardActionUI, CardMoveUIType} from '../../../api/stats/contracts'
import {useVerticalGroupedBy} from '../../../features/grouping/hooks/use-vertical-grouped-by'
import {isPlatformMeta} from '../../../helpers/util'
import {useSidePanel} from '../../../hooks/use-side-panel'
import {useVisibleFields} from '../../../hooks/use-visible-fields'
import {isBoardLabelableColumn} from '../../../models/column-capabilities'
import type {SubIssuesProgressColumnModel} from '../../../models/column-model/system/sub-issues-progress'
import {AgentTaskPill, useAgentTaskPillsToShow} from '../../fields/agent-task-pill'
import {useCardSelection} from '../hooks/use-card-selection'
import styles from './card-internal-content.module.css'
import {CardLabel} from './card-label'
import {Header} from './header'
import {Title} from './title'
import type {CardProps} from './types'

export const CardInternalContent = memo(function CardInternalContentComponent({
  item,
  verticalGroupId,
  archiveItem,
  removeItem,
  moveItemToTop,
  moveItemToBottom,
  contextMenuRef,
  isDragging = false,
}: Pick<CardProps, 'item'> & {
  verticalGroupId?: string
  archiveItem?: (ui: BoardActionUI) => void
  removeItem: (ui: BoardActionUI) => void
  moveItemToTop?: (ui: CardMoveUIType) => void
  moveItemToBottom?: (ui: CardMoveUIType) => void
  contextMenuRef: RefObject<HTMLDivElement | null>
  isDragging?: boolean
}) {
  const {groupedByColumnId} = useVerticalGroupedBy()
  const {visibleFields} = useVisibleFields()
  const {state: selectionState} = useCardSelection()

  const agentTaskPills = useAgentTaskPillsToShow({agentTasks: item.agentTasks})
  const {openPaneAgentTask} = useSidePanel()

  const repoId = item.ownerId()
  const handleAgentTaskClick = useCallback(
    (taskId: string, e: React.MouseEvent) => {
      // Allow modified clicks and middle-click to open in new tab
      if (isPlatformMeta(e) || e.button === 1) return
      e.preventDefault()
      openPaneAgentTask(taskId, repoId)
    },
    [openPaneAgentTask, repoId],
  )

  // Sub-issues progress is a special case in that that it fills the entire width of the card
  // and should always be rendered at the bottom of the card when the field is visible.
  const subIssuesProgressField = visibleFields.find(
    (f): f is SubIssuesProgressColumnModel => f.id === SystemColumnId.SubIssuesProgress,
  )
  const fieldsToDisplay = visibleFields.filter(
    f => f.id !== groupedByColumnId && isBoardLabelableColumn(f.dataType) && f.id !== subIssuesProgressField?.id,
  )

  const columnData = item.columns
  const hasFieldsToShow = fieldsToDisplay.length > 0 || subIssuesProgressField !== undefined
  const hasSubIssues = Number(columnData['Sub-issues progress']?.total) > 0

  const isRedactedItem = item.contentType === ItemType.RedactedItem
  const isSelected = !isRedactedItem && !!selectionState[item.id]

  const fieldsDescriptionId = useId()

  return (
    <div className={clsx(styles.Box, isSelected && styles.isSelected, isRedactedItem && styles.isRedacted)}>
      <div className={styles.Box_1}>
        <Header
          {...testIdProps(`board-card-header`)}
          item={item}
          verticalGroupId={verticalGroupId}
          columnData={columnData}
          className={styles.Header}
          archiveItem={archiveItem}
          removeItem={removeItem}
          moveItemToTop={moveItemToTop}
          moveItemToBottom={moveItemToBottom}
          contextMenuRef={contextMenuRef}
          disableContextMenu={isDragging}
        />
        <Title item={item} disableFocus={isDragging} />
      </div>
      {(hasFieldsToShow || agentTaskPills.length > 0) && (
        <>
          <ul
            {...testIdProps(`card-labels`)}
            aria-label="Fields"
            aria-describedby={fieldsDescriptionId}
            className={styles.Box_2}
          >
            {fieldsToDisplay.map(field => (
              <CardLabel key={field.id} field={field} item={item} columnData={columnData} />
            ))}

            {subIssuesProgressField && hasSubIssues && (
              <CardLabel
                key={subIssuesProgressField.id}
                field={subIssuesProgressField}
                item={item}
                columnData={columnData}
              />
            )}
            {agentTaskPills.map(task => {
              // Filter all tasks for this specific agent so the popover can show the full list
              const tasksForAgent = item.agentTasks?.filter(t => t.agent?.login === task.agent?.login)
              return (
                <li key={task.id ?? task.agent?.login ?? 'copilot'}>
                  <AgentTaskPill
                    state={task.state}
                    agent={task.agent}
                    agentTasks={tasksForAgent ?? [task]}
                    onTaskClick={handleAgentTaskClick}
                  />
                </li>
              )
            })}
          </ul>
          <span className="sr-only" id={fieldsDescriptionId}>
            Click a value to filter the view
          </span>
        </>
      )}
    </div>
  )
})
